import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useDevices } from '../store/devices';
import { useLiveEvents } from '../hooks/useLiveEvents';
import Guacamole from 'guacamole-common-js';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API || '';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  {
    urls: ['turn:rmm.docmail.cloud:3478?transport=udp', 'turn:rmm.docmail.cloud:3478?transport=tcp'],
    username: 'classrmm',
    credential: 'dcbac3b236838f7b4450711c9348e5ed188ea2b0a957d56e',
  },
];

export default function RemoteControl() {
  const { deviceId } = useParams();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const isPopup = new URLSearchParams(window.location.search).get('popup') === '1'
    || new URLSearchParams(window.location.hash.split('?')[1] || '').get('popup') === '1';
  const { devices, fetch: fetchDevices } = useDevices();
  const device = devices.find(d => d.device_id === deviceId);
  const deviceName = device ? (device.label || device.hostname || deviceId.slice(0, 8)) : deviceId.slice(0, 8);

  const videoRef      = useRef(null);
  const streamImgRef  = useRef(null);   // JPEG live stream img element
  const pcRef         = useRef(null);
  const dcRef         = useRef(null);   // WebRTC data channel for P2P mouse input
  const offerTimeoutRef = useRef(null); // cleared when agent offer arrives
  const lastMoveRef   = useRef(0);      // throttle mousemove to 30/sec
  const containerRef  = useRef(null);
  const rdpContRef    = useRef(null);
  const guacCanvasRef = useRef(null);  // separate from React DOM — safe to innerHTML
  const rdpClientRef  = useRef(null);
  const rdpKeyRef     = useRef(null);
  const fpsFrames     = useRef(0);
  const rdpPhaseRef   = useRef('idle');
  // Direct VNC / HVNC stream refs
  const vncWsRef      = useRef(null);   // WebSocket for VNC stream view
  const hvncWsRef     = useRef(null);   // WebSocket for HVNC stream view
  const vncImgRef     = useRef(null);   // <img> for VNC frames only
  const hvncImgRef    = useRef(null);   // <img> for HVNC frames only — separate so coords never mix
  const modeRef       = useRef('idle'); // tracks current mode without stale closures
  const vncStoppingRef  = useRef(false); // set true on manual stop to suppress auto-reconnect
  const hvncStoppingRef = useRef(false);
  // Stored once, reused for both RDP modes
  const rdpCredsRef   = useRef({ username: '', password: '' });
  // Pending RDP mode after creds are entered: 'session' | 'share'
  const pendingRdpMode = useRef(null);
  // Auto-relay fallback tracking
  const webrtcModeRef       = useRef('auto');   // kept in sync to avoid stale closures
  const prevRtpStatsRef     = useRef(null);      // { packetsLost, packetsReceived, framesDecoded }
  const frozenPollsRef      = useRef(0);         // consecutive polls with no new video frames
  const fallbackTriggeredRef = useRef(false);    // prevents repeated relay auto-fallback

  const [hvncCmd,       setHvncCmd]       = useState('charmap.exe');
  const [hvncInitState, setHvncInitState] = useState('idle'); // idle | running | done | error
  const [mode,          setMode]          = useState('idle');
  const [blanked,       setBlanked]       = useState(false);
  const [fps,           setFps]           = useState(0);
  const [frameCount,    setFrameCount]    = useState(0);
  const [streamPreset,  setStreamPreset]  = useState('normal');
  const [webrtcMode,    setWebrtcMode]    = useState('auto');
  const [fullscreen,    setFullscreen]    = useState(false);
  const [webrtcState,   setWebrtcState]   = useState('');
  const [webrtcDiag,    setWebrtcDiag]    = useState({
    offerReceived: false,
    offerAt: '',
    answerAt: '',
    signalingState: 'idle',
    iceGatheringState: 'idle',
    iceConnectionState: 'idle',
    connectionState: 'idle',
    selectedPath: 'unknown',
    selectedLocalType: '',
    selectedRemoteType: '',
    statsAt: '',
    error: '',
  });
  const [rdpPhase,      setRdpPhase]      = useState('idle');
  // Credentials modal
  const [showCreds,     setShowCreds]     = useState(false);
  const [credUsername,  setCredUsername]  = useState('');
  const [credPassword,  setCredPassword]  = useState('');
  const [credsStored,   setCredsStored]   = useState(false);
  const [rdpLoaded,     setRdpLoaded]     = useState(false);  // true after first display resize
  // Quick RDP access — persisted per device in localStorage
  const rdpStorageKey = `classrmm_rdp_${deviceId}`;
  const [rdpAccessCreds, setRdpAccessCreds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(rdpStorageKey));
      // Clear tunnel state on load — tunnel is session-level, not persistent
      if (saved) { saved.tunnel_status = 'none'; saved.tunnel_port = null; }
      return saved || null;
    } catch { return null; }
  });
  const [rdpAccessLoading, setRdpAccessLoading] = useState(false);

  // Persist RDP creds to localStorage (exclude tunnel state)
  useEffect(() => {
    if (rdpAccessCreds) {
      const toSave = { ...rdpAccessCreds };
      delete toSave.tunnel_status;
      delete toSave.tunnel_port;
      localStorage.setItem(rdpStorageKey, JSON.stringify(toSave));
    }
  }, [rdpAccessCreds, rdpStorageKey]);

  const setRdpPhaseBoth = (v) => { rdpPhaseRef.current = v; setRdpPhase(v); };

  // ── Fullscreen ──────────────────────────────────────────────────────────────
  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  };
  useEffect(() => {
    const h = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);
  // Keep webrtcModeRef current so closures in pollStats can read the live value.
  useEffect(() => { webrtcModeRef.current = webrtcMode; }, [webrtcMode]);
  // Keep modeRef current so sendInput and keyboard handler can read mode without stale closures.
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // ── Keyboard pass-through for Stream / WebRTC modes ─────────────────────────
  // Maps browser KeyboardEvent.code (physical key, layout-independent) → pyautogui key name
  const _KEY_MAP = {
    KeyA:'a',KeyB:'b',KeyC:'c',KeyD:'d',KeyE:'e',KeyF:'f',KeyG:'g',KeyH:'h',
    KeyI:'i',KeyJ:'j',KeyK:'k',KeyL:'l',KeyM:'m',KeyN:'n',KeyO:'o',KeyP:'p',
    KeyQ:'q',KeyR:'r',KeyS:'s',KeyT:'t',KeyU:'u',KeyV:'v',KeyW:'w',KeyX:'x',
    KeyY:'y',KeyZ:'z',
    Digit0:'0',Digit1:'1',Digit2:'2',Digit3:'3',Digit4:'4',
    Digit5:'5',Digit6:'6',Digit7:'7',Digit8:'8',Digit9:'9',
    Numpad0:'num0',Numpad1:'num1',Numpad2:'num2',Numpad3:'num3',Numpad4:'num4',
    Numpad5:'num5',Numpad6:'num6',Numpad7:'num7',Numpad8:'num8',Numpad9:'num9',
    NumpadAdd:'add',NumpadSubtract:'subtract',NumpadMultiply:'multiply',
    NumpadDivide:'divide',NumpadDecimal:'decimal',NumpadEnter:'enter',
    F1:'f1',F2:'f2',F3:'f3',F4:'f4',F5:'f5',F6:'f6',
    F7:'f7',F8:'f8',F9:'f9',F10:'f10',F11:'f11',F12:'f12',
    Backspace:'backspace',Tab:'tab',Enter:'enter',Escape:'esc',Space:'space',
    Delete:'delete',Insert:'insert',
    Home:'home',End:'end',PageUp:'pageup',PageDown:'pagedown',
    ArrowLeft:'left',ArrowRight:'right',ArrowUp:'up',ArrowDown:'down',
    ShiftLeft:'shiftleft',ShiftRight:'shiftright',
    ControlLeft:'ctrlleft',ControlRight:'ctrlright',
    AltLeft:'altleft',AltRight:'altright',
    MetaLeft:'winleft',MetaRight:'winright',
    CapsLock:'capslock',NumLock:'numlock',ScrollLock:'scrolllock',
    Minus:'-',Equal:'=',BracketLeft:'[',BracketRight:']',
    Backslash:'\\',Semicolon:';',Quote:"'",Backquote:'`',
    Comma:',',Period:'.',Slash:'/',
  };

  useEffect(() => {
    const t = setInterval(() => { setFps(fpsFrames.current); fpsFrames.current = 0; }, 1000);
    return () => clearInterval(t);
  }, []);

  // In popup mode fetch devices so we can display the device name in the title bar
  useEffect(() => {
    if (isPopup && devices.length === 0) fetchDevices();
  }, []); // eslint-disable-line

  // ── WebRTC signal handler ───────────────────────────────────────────────────
  const _handleWebRTCSignal = useCallback(async (sig) => {
    const pc = pcRef.current;
    if (!pc) return;
    if (sig.type === 'error') {
      setWebrtcDiag(prev => ({ ...prev, error: sig.message || 'Agent-side WebRTC error' }));
      toast.error(sig.message || 'Agent could not start WebRTC');
      return;
    }
    if (sig.type === 'offer') {
      setWebrtcDiag(prev => ({
        ...prev,
        offerReceived: true,
        offerAt: new Date().toISOString(),
        error: '',
      }));
      clearTimeout(offerTimeoutRef.current); offerTimeoutRef.current = null;
      try {
        await pc.setRemoteDescription({ type: 'offer', sdp: sig.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        // Wait for ICE gathering to complete so relay candidates are embedded in the SDP.
        // This avoids the trickle-ICE race where relay candidates arrive at the agent before
        // the answer, get silently dropped by aiortc, and ICE fails after 64s.
        await new Promise((resolve) => {
          if (pc.iceGatheringState === 'complete') { resolve(); return; }
          const onGather = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', onGather); resolve(); } };
          pc.addEventListener('icegatheringstatechange', onGather);
          setTimeout(resolve, 5000); // cap wait at 5s in case TURN is slow
        });
        // Send pc.localDescription.sdp (updated with all gathered candidates), not the original answer.sdp
        wsSendRef.current({ type: 'webrtc_signal', device_id: deviceId, data: { type: 'answer', sdp: pc.localDescription.sdp } });
        setWebrtcDiag(prev => ({ ...prev, answerAt: new Date().toISOString() }));
      } catch (err) {
        console.error('WebRTC answer error:', err);
        setWebrtcDiag(prev => ({ ...prev, error: String(err?.message || err || 'answer error') }));
        toast.error('WebRTC negotiation failed');
      }
    } else if (sig.type === 'candidate' && sig.candidate && sig.candidate.candidate) {
      try { await pc.addIceCandidate(sig.candidate); } catch (err) { console.debug('ICE:', err); }
    }
  }, [deviceId]);

  const wsSendRef = useRef(null);

  // ── Guacamole connect (shared setup) ────────────────────────────────────────
  const _buildGuacClient = useCallback((wsUrl, connectParams) => {
    if (rdpClientRef.current) {
      try { rdpClientRef.current.disconnect(); } catch (_) {}
      rdpClientRef.current = null;
    }
    if (rdpKeyRef.current) {
      try { rdpKeyRef.current.onkeydown = null; rdpKeyRef.current.onkeyup = null; } catch (_) {}
      rdpKeyRef.current = null;
    }
    const tunnel = new Guacamole.WebSocketTunnel(wsUrl);
    const client = new Guacamole.Client(tunnel);
    rdpClientRef.current = client;
    setRdpPhaseBoth('connecting');
    setRdpLoaded(false);
    const display   = client.getDisplay();
    const displayEl = display.getElement();
    displayEl.style.display  = 'block';
    displayEl.style.maxWidth = '100%';
    displayEl.style.maxHeight = '100%';
    displayEl.style.overflow = 'hidden';
    if (guacCanvasRef.current) { guacCanvasRef.current.innerHTML = ''; guacCanvasRef.current.appendChild(displayEl); }
    display.onresize = (dw, dh) => {
      const cont = rdpContRef.current;
      if (!cont || !dw) return;
      const cw = cont.clientWidth;
      const ch = cont.clientHeight;
      const scale = Math.min(cw / dw, ch / dh);
      display.scale(scale);
      setRdpLoaded(true);
    };
    const mouse = new Guacamole.Mouse(displayEl);
    mouse.onmousedown = mouse.onmousemove = mouse.onmouseup = (ms) => client.sendMouseState(ms);
    const keyboard = new Guacamole.Keyboard(document);
    keyboard.onkeydown = (k) => client.sendKeyEvent(1, k);
    keyboard.onkeyup   = (k) => client.sendKeyEvent(0, k);
    rdpKeyRef.current  = keyboard;
    client.onstatechange = (state) => {
      if (state === Guacamole.Client.State.CONNECTED) { setRdpPhaseBoth('connected'); toast.success('Session active'); }
      else if (state === Guacamole.Client.State.DISCONNECTED || state === Guacamole.Client.State.CLOSED) {
        setRdpPhaseBoth('idle'); setMode(m => m === 'rdp' ? 'idle' : m);
      }
    };
    client.onerror  = (err) => { toast.error('Session error: ' + (err.message || JSON.stringify(err))); setRdpPhaseBoth('error'); };
    tunnel.onerror  = (err) => { toast.error('Tunnel error: '  + (err.message || JSON.stringify(err))); setRdpPhaseBoth('error'); };
    client.connect(connectParams);
  }, [deviceId]);

  const _wsBase = useCallback(() => {
    const wsProto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const base = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API;
    return base
      ? base.replace(/^https?/, wsProto === 'wss' ? 'wss' : 'ws')
      : `${wsProto}://${window.location.host}`;
  }, []);

  // ── RDP connect (with credentials) ──────────────────────────────────────────
  const connectGuacamole = useCallback((rdpPort, consoleSession) => {
    const { username, password } = rdpCredsRef.current;
    const token   = authHeaders().Authorization?.split(' ')[1] || '';
    const wsHost  = _wsBase();
    const w = rdpContRef.current ? rdpContRef.current.clientWidth  || 1280 : 1280;
    const h = rdpContRef.current ? rdpContRef.current.clientHeight || 800  : 800;
    const wsUrl = `${wsHost}/rdp/ws/${deviceId}`;

    const u = encodeURIComponent(username);
    const p = encodeURIComponent(password);
    const con = consoleSession ? 'true' : 'false';
    _buildGuacClient(wsUrl, `token=${token}&width=${w}&height=${h}&rdp_port=${rdpPort}&username=${u}&password=${p}&console=${con}`);
  }, [deviceId, authHeaders, _wsBase, _buildGuacClient]);

  // ── VNC connect (no credentials) ────────────────────────────────────────────
  const connectVNC = useCallback((vncPort) => {
    const token  = authHeaders().Authorization?.split(' ')[1] || '';
    const wsHost = _wsBase();
    const w = rdpContRef.current ? rdpContRef.current.clientWidth  || 1280 : 1280;
    const h = rdpContRef.current ? rdpContRef.current.clientHeight || 800  : 800;
    const wsUrl = `${wsHost}/vnc/ws/${deviceId}`;
    _buildGuacClient(wsUrl, `token=${token}&width=${w}&height=${h}&vnc_port=${vncPort}`);
  }, [deviceId, authHeaders, _wsBase, _buildGuacClient]);

  // ── Live events ─────────────────────────────────────────────────────────────
  const { send: wsSend } = useLiveEvents(useCallback((e) => {
    if (e.event === 'stream_frame' && e.device_id === deviceId) {
      // Only update fps/state if we are actually in stream mode (not WebRTC)
      if (streamImgRef.current && e.image) {
        streamImgRef.current.src = 'data:image/jpeg;base64,' + e.image;
        // useRef to get current mode without stale closure
        if (streamImgRef._mode === 'stream') {
          fpsFrames.current++;
          setWebrtcState('connected');
        }
      }
    } else if (e.event === 'webrtc_signal' && e.device_id === deviceId) {
      _handleWebRTCSignal(e.data);
    } else if (e.event === 'shell_result' && e.device_id === deviceId && typeof e.stdout === 'string') {
      const out = e.stdout.trim();
      if (out.startsWith('RDP_RESULT|')) {
        const parts = out.split('|');
        const steps = (parts[3] || '').split(',');
        const localIp = parts[5] || '';
        const allOk = steps.every(s => s.startsWith('STEP') && s.includes('OK'));
        setRdpAccessCreds(prev => prev ? { ...prev, username: parts[1], password: parts[2], pending: false, steps, verified: allOk, local_ip: localIp || prev.local_ip } : prev);
        if (allOk) {
          toast.success('RDP ready — user created, RDP enabled, verified');
        } else {
          toast.error('RDP setup had issues: ' + steps.filter(s => s.includes('FAIL')).join(', ') + (parts[4] ? ' — ' + parts[4] : ''));
        }
      } else if (out === 'RDP_REMOVED') {
        setRdpAccessCreds(null);
        toast.success('RDP access removed');
      }
    } else if (e.event === 'clipboard_content' && e.device_id === deviceId) {
      if (e.text) {
        navigator.clipboard.writeText(e.text).then(() => toast.success('Remote clipboard copied to your clipboard'));
      } else {
        toast('Remote clipboard is empty');
      }
    } else if (e.event === 'clipboard_set_result' && e.device_id === deviceId) {
      if (!e.ok) toast.error('Failed to set remote clipboard');
    } else if (e.event === 'tunnel_ready' && e.device_id === deviceId) {
      toast.success('Tunnel ready');
      // Update RDP card with tunnel ready status
      setRdpAccessCreds(prev => prev ? { ...prev, tunnel_port: e.port, tunnel_status: 'ready' } : prev);
      if (rdpPhaseRef.current === 'tunnel' && pendingRdpMode.current === 'session') {
        connectGuacamole(e.port, false);
      }
    } else if (e.event === 'tunnel_error' && e.device_id === deviceId) {
      if (rdpPhaseRef.current !== 'idle') { toast.error('Tunnel error: ' + e.error); setRdpPhaseBoth('error'); }
    } else if (e.event === 'tunnel_stopped' && e.device_id === deviceId) {
      // Only kill session if we're in RDP mode, not VNC/screen share
      if (pendingRdpMode.current !== 'share') {
        if (rdpClientRef.current) { try { rdpClientRef.current.disconnect(); } catch (_) {} rdpClientRef.current = null; }
        setRdpPhaseBoth('idle');
      }
    } else if (e.event === 'vnc_tunnel_ready' && e.device_id === deviceId) {
      toast.success('Screen Share ready');
      if (rdpPhaseRef.current === 'tunnel' && pendingRdpMode.current === 'share') {
        connectVNC(e.vps_port || 0);
      }
    } else if (e.event === 'vnc_tunnel_error' && e.device_id === deviceId) {
      toast.error('Screen Share error: ' + (e.error || 'tunnel failed'));
      pendingRdpMode.current = 'share';  // ensure retry button calls startScreenShare
      setMode('rdp');
      setRdpPhaseBoth('error');
    }
  }, [deviceId, _handleWebRTCSignal, connectGuacamole, connectVNC]));

  useEffect(() => { wsSendRef.current = wsSend; }, [wsSend]);

  // ── Generic command helper ───────────────────────────────────────────────────
  const cmd = async (endpoint, body) => {
    try {
      let jsonBody;
      try {
        jsonBody = JSON.stringify(body || {});
      } catch (err) {
        // Defensive log for circular structure
        console.error('[cmd] JSON.stringify error:', err, body);
        toast.error('Command payload not serializable (circular structure)');
        throw err;
      }
      const res = await fetch(API + '/cmd/' + deviceId + '/' + endpoint, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: jsonBody,
      });
      if (!res.ok) {
        let detail = 'Command failed';
        try { detail = (await res.json()).detail || detail; } catch (_) { detail = `Server error (${res.status})`; }
        throw new Error(detail);
      }
      return true;
    } catch (e) {
      toast.error(e.message || 'Device offline or agent not running');
      return false;
    }
  };

  // ── WebRTC ──────────────────────────────────────────────────────────────────
  // ── JPEG Live Stream (5fps, no WebRTC/TURN needed) ─────────────────────────
  const startLiveStream = async () => {
    setMode('stream'); setWebrtcState('connecting');
    if (streamImgRef.current) streamImgRef._mode = 'stream';
    const ok = await cmd('stream/start', { profile: streamPreset });
    if (!ok) { setMode('idle'); setWebrtcState(''); streamImgRef._mode = ''; }
    else { fpsFrames.current = 0; setFrameCount(0); toast('Live stream starting…'); }
  };
  const stopLiveStream = async () => {
    streamImgRef._mode = '';
    await cmd('stream/stop');
    setMode('idle'); setWebrtcState('');
    if (streamImgRef.current) streamImgRef.current.src = '';
    toast('Stream stopped');
  };

  const startWebRTC = async (forceMode = null) => {
    // Defensive: ignore accidental DOM event as first argument
    let modeArg = forceMode;
    if (modeArg && typeof modeArg === 'object' && (modeArg.constructor?.name?.endsWith('Event') || modeArg.target)) {
      modeArg = null;
    }
    const activeMode = modeArg || webrtcMode;
    const selectedProfile = activeMode === 'jpeg' ? 'jpeg_fast' : 'normal';
    // Reset fallback state only on explicit user action (forceMode===null means user clicked button)
    if (!modeArg) {
      fallbackTriggeredRef.current = false;
      prevRtpStatsRef.current = null;
      frozenPollsRef.current = 0;
    }
    setWebrtcDiag({
      offerReceived: false,
      offerAt: '',
      answerAt: '',
      signalingState: 'new',
      iceGatheringState: 'new',
      iceConnectionState: 'new',
      connectionState: 'new',
      selectedPath: 'unknown',
      selectedLocalType: '',
      selectedRemoteType: '',
      statsAt: '',
      error: '',
    });
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;
    pc.ontrack = (e) => {
      if (videoRef.current && e.streams[0]) {
        videoRef.current.srcObject = e.streams[0];
        videoRef.current.play().catch(() => {});
        // Count video frames for the fps display
        const vid = videoRef.current;
        if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
          const onFrame = () => {
            fpsFrames.current++;
            setFrameCount(c => c + 1);
            if (vid.srcObject) vid.requestVideoFrameCallback(onFrame);
          };
          vid.requestVideoFrameCallback(onFrame);
        }
      }
      fpsFrames.current = 0; setFrameCount(0);
      setWebrtcState('connected'); setMode('webrtc');
      toast.success('WebRTC connected!');
    };
    // Receive the control data channel created by the agent
    pc.ondatachannel = (e) => {
      const dc = e.channel;
      dcRef.current = dc;
      dc.onopen  = () => logger.debug && console.log('[WebRTC] control channel open');
      dc.onclose = () => { dcRef.current = null; };
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) wsSendRef.current({
        type: 'webrtc_signal', device_id: deviceId,
        data: { type: 'candidate', candidate: { candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid, sdpMLineIndex: e.candidate.sdpMLineIndex } },
      });
    };
    pc.onconnectionstatechange = () => {
      const cs = pc.connectionState;
      setWebrtcState(cs);
      setWebrtcDiag(prev => ({ ...prev, connectionState: cs || prev.connectionState }));
      if (cs === 'failed') {
        if (!fallbackTriggeredRef.current && activeMode !== 'relay') {
          fallbackTriggeredRef.current = true;
          toast('Connection failed — auto-switching to TURN Relay', { icon: '⚠️' });
          setWebrtcDiag(prev => ({ ...prev, error: 'Auto-fallback: connection failed, retrying via TURN relay' }));
          pc.onicecandidate = null; pc.close(); pcRef.current = null;
          if (dcRef.current) { try { dcRef.current.close(); } catch (_) {} dcRef.current = null; }
          clearTimeout(offerTimeoutRef.current);
          if (videoRef.current) videoRef.current.srcObject = null;
          setTimeout(async () => {
            try { await cmd('webrtc/stop'); } catch (_) {}
            setMode('idle'); setWebrtcState('');
            setWebrtcMode('relay');
            startWebRTC('relay');
          }, 600);
        } else {
          toast.error('WebRTC connection lost'); setMode('idle'); setWebrtcState('failed');
        }
      } else if (cs === 'disconnected') {
        toast.error('WebRTC disconnected'); setMode('idle'); setWebrtcState('failed');
      }
    };
    pc.onsignalingstatechange = () => {
      setWebrtcDiag(prev => ({ ...prev, signalingState: pc.signalingState || prev.signalingState }));
    };
    pc.onicegatheringstatechange = () => {
      setWebrtcDiag(prev => ({ ...prev, iceGatheringState: pc.iceGatheringState || prev.iceGatheringState }));
    };
    pc.oniceconnectionstatechange = () => {
      setWebrtcDiag(prev => ({ ...prev, iceConnectionState: pc.iceConnectionState || prev.iceConnectionState }));
    };
    setWebrtcState('connecting'); setMode('webrtc');
    // Attempt to claim the WebRTC session — 409 means another admin is already connected
    let startOk = false;
    try {
      const res = await fetch(API + '/cmd/' + deviceId + '/webrtc/start', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile: selectedProfile, network_mode: activeMode }),
      });
      if (res.status === 409) {
        await res.json();
        toast.error('Remote control session already active');
      } else if (!res.ok) {
        toast.error((await res.json()).detail || 'Command failed');
      } else {
        startOk = true;
      }
    } catch (e) {
      toast.error(e.message || 'Device offline or agent not running');
    }
    if (!startOk) { pc.close(); pcRef.current = null; setMode('idle'); setWebrtcState(''); return; }
    toast('Waiting for WebRTC offer from agent...');
    offerTimeoutRef.current = setTimeout(() => {
      if (pcRef.current) {
        pcRef.current.onicecandidate = null;
        pcRef.current.close();
        pcRef.current = null;
      }
      cmd('webrtc/stop');
      setMode('idle'); setWebrtcState('');
      setWebrtcDiag(prev => ({
        ...prev,
        error: 'No offer received within timeout (agent/session/dependency/network issue)',
      }));
      toast.error('No WebRTC offer received from agent. Check user agent status, TURN reachability, and dependency bundle in the installed agent build.');
    }, 12000);
  };
  const stopWebRTC = async () => {
    clearTimeout(offerTimeoutRef.current); offerTimeoutRef.current = null;
    if (dcRef.current)  { try { dcRef.current.close(); } catch (_) {} dcRef.current = null; }
    if (pcRef.current)  { pcRef.current.onicecandidate = null; pcRef.current.close(); pcRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    await cmd('webrtc/stop'); setMode('idle'); setWebrtcState('');
    toast('WebRTC stopped');
  };

  // Poll selected candidate pair so we can show direct vs relay in diagnostics.
  useEffect(() => {
    if (mode !== 'webrtc' || !pcRef.current) return;
    let cancelled = false;

    const pollStats = async () => {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        const stats = await pc.getStats();
        let pair = null;

        stats.forEach(r => {
          if (!pair && r.type === 'transport' && r.selectedCandidatePairId && stats.get(r.selectedCandidatePairId)) {
            pair = stats.get(r.selectedCandidatePairId);
          }
        });
        if (!pair) {
          stats.forEach(r => {
            if (!pair && r.type === 'candidate-pair' && r.selected) pair = r;
          });
        }

        let path = 'unknown';
        let localType = '';
        let remoteType = '';
        if (pair) {
          const local = pair.localCandidateId ? stats.get(pair.localCandidateId) : null;
          const remote = pair.remoteCandidateId ? stats.get(pair.remoteCandidateId) : null;
          localType = local?.candidateType || '';
          remoteType = remote?.candidateType || '';
          path = (localType === 'relay' || remoteType === 'relay') ? 'relay' : 'direct';
        }

        if (!cancelled) {
          setWebrtcDiag(prev => ({
            ...prev,
            selectedPath: path,
            selectedLocalType: localType,
            selectedRemoteType: remoteType,
            statsAt: new Date().toISOString(),
          }));
        }
        // ── Auto-relay fallback on packet loss or stream freeze ────────────
        if (!fallbackTriggeredRef.current && webrtcModeRef.current !== 'relay' && !cancelled) {
          let rtpStat = null;
          stats.forEach(r => { if (r.type === 'inbound-rtp' && r.kind === 'video') rtpStat = r; });
          if (rtpStat) {
            const prv = prevRtpStatsRef.current;
            if (prv) {
              const lostDelta  = (rtpStat.packetsLost     || 0) - prv.packetsLost;
              const recvDelta  = (rtpStat.packetsReceived || 0) - prv.packetsReceived;
              const total      = recvDelta + lostDelta;
              const lossRatio  = total > 5 ? lostDelta / total : 0;
              const frameDelta = (rtpStat.framesDecoded   || 0) - prv.framesDecoded;
              frozenPollsRef.current = frameDelta === 0 ? frozenPollsRef.current + 1 : 0;
              const frozen   = frozenPollsRef.current >= 3;  // ~6 s of no new frames
              const highLoss = lossRatio > 0.08;             // >8 % packet loss
              if ((highLoss || frozen) && path !== 'relay') {
                fallbackTriggeredRef.current = true;
                const reason = frozen ? 'stream frozen' : `${Math.round(lossRatio * 100)}% packet loss`;
                toast(`⚠️ Network degraded (${reason}) — switching to TURN Relay`);
                setWebrtcDiag(p => ({ ...p, error: `Auto-fallback to relay: ${reason}` }));
                const curPc = pcRef.current;
                if (curPc) { curPc.onicecandidate = null; curPc.close(); pcRef.current = null; }
                if (dcRef.current) { try { dcRef.current.close(); } catch (_) {} dcRef.current = null; }
                clearTimeout(offerTimeoutRef.current);
                if (videoRef.current) videoRef.current.srcObject = null;
                cancelled = true;
                setTimeout(async () => {
                  try { await cmd('webrtc/stop'); } catch (_) {}
                  setMode('idle'); setWebrtcState('');
                  setWebrtcMode('relay');
                  startWebRTC('relay');
                }, 600);
              }
            }
            prevRtpStatsRef.current = {
              packetsLost:     rtpStat.packetsLost     || 0,
              packetsReceived: rtpStat.packetsReceived || 0,
              framesDecoded:   rtpStat.framesDecoded   || 0,
            };
          }
        }
      } catch {
        // Ignore intermittent stats failures
      }
    };

    pollStats();
    const i = setInterval(pollStats, 2000);
    return () => { cancelled = true; clearInterval(i); };
  }, [mode]);

  // Open this page in a separate floating window and return parent to device detail
  const popOut = () => {
    const base = window.location.href.split('#')[0].replace(/\?.*$/, '');
    const popupUrl = `${base}?popup=1#/device/${deviceId}/remote`;
    window.open(popupUrl, `rmm_remote_${deviceId}`);
    navigate(`/device/${deviceId}`);
  };

  // Send mouse/keyboard input — routes to the correct channel based on current mode.
  const sendInput = useCallback((payload) => {
    let jsonPayload;
    try {
      jsonPayload = JSON.stringify(payload);
    } catch (err) {
      console.error('[sendInput] JSON.stringify error:', err, payload);
      return;
    }
    const m = modeRef.current;
    if (m === 'vnc_stream' && vncWsRef.current?.readyState === WebSocket.OPEN) {
      vncWsRef.current.send(jsonPayload);
    } else if (m === 'hvnc' && hvncWsRef.current?.readyState === WebSocket.OPEN) {
      hvncWsRef.current.send(jsonPayload);
    } else {
      const dc = dcRef.current;
      if (dc && dc.readyState === 'open') {
        dc.send(jsonPayload);
      } else if (wsSendRef.current) {
        wsSendRef.current({ type: 'webrtc_input', device_id: deviceId, data: payload });
      }
    }
  }, [deviceId]);

  // ── Keyboard pass-through for Stream / WebRTC / VNC / HVNC modes ───────────
  useEffect(() => {
    if (mode !== 'stream' && mode !== 'webrtc' && mode !== 'vnc_stream' && mode !== 'hvnc') return;
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const key = _KEY_MAP[e.code];
      if (!key) return;
      e.preventDefault();
      // Include the actual typed character (accounts for Shift, CapsLock, AltGr, etc.)
      const char = (e.type === 'keydown' && e.key.length === 1) ? e.key : undefined;
      sendInput({ event: e.type === 'keydown' ? 'keydown' : 'keyup', key, ...(char ? { char } : {}) });
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('keyup',   onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('keyup',   onKey);
    };
  }, [mode, sendInput]);

  // ── Direct VNC Stream ────────────────────────────────────────────────────────
  const _openStreamWS = useCallback((wsPath, wsRef, modeName, imgRef, stoppingRef) => {
    if (stoppingRef.current) return;
    const token   = authHeaders().Authorization?.split(' ')[1] || '';
    const wsProto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const base    = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API;
    const wsBase  = base
      ? base.replace(/^https?/, wsProto === 'wss' ? 'wss' : 'ws')
      : `${wsProto}://${window.location.host}`;
    const ws = new WebSocket(`${wsBase}${wsPath}?token=${token}`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      fpsFrames.current = 0;
      setWebrtcState('connected');
    };

    ws.onmessage = (event) => {
      if (!(event.data instanceof ArrayBuffer)) return;
      fpsFrames.current++;
      setFrameCount(c => c + 1);
      const imgEl = imgRef.current;
      if (!imgEl) return;
      const prev = imgEl._blobUrl;
      imgEl._blobUrl = URL.createObjectURL(new Blob([event.data], { type: 'image/jpeg' }));
      imgEl.src = imgEl._blobUrl;
      if (prev) URL.revokeObjectURL(prev);
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (stoppingRef.current || modeRef.current !== modeName) {
        setMode(m => (m === modeName ? 'idle' : m));
        setWebrtcState('');
      } else {
        // Unexpected close — auto-reconnect after 2s
        setWebrtcState('reconnecting');
        setTimeout(() => {
          if (!stoppingRef.current && modeRef.current === modeName) {
            _openStreamWS(wsPath, wsRef, modeName, imgRef, stoppingRef);
          }
        }, 2000);
      }
    };

    ws.onerror = () => {
      wsRef.current = null;
      // onclose fires after onerror so reconnect is handled there
    };
  }, [authHeaders]);  // eslint-disable-line

  const startVNCStream = useCallback(async () => {
    vncStoppingRef.current = false;
    setMode('vnc_stream');
    setWebrtcState('connecting');
    const ok = await cmd('vnc_stream/start', { fps: 25, quality: 40 });
    if (!ok) { setMode('idle'); setWebrtcState(''); return; }
    _openStreamWS(`/vnc/stream/view/${deviceId}`, vncWsRef, 'vnc_stream', vncImgRef, vncStoppingRef);
  }, [deviceId, _openStreamWS]);  // eslint-disable-line

  const stopVNCStream = useCallback(async () => {
    vncStoppingRef.current = true;
    if (vncWsRef.current) { vncWsRef.current.close(); vncWsRef.current = null; }
    await cmd('vnc_stream/stop');
    setMode('idle'); setWebrtcState('');
    toast('VNC stream stopped');
  }, []);  // eslint-disable-line

  const startHVNC = useCallback(async () => {
    hvncStoppingRef.current = false;
    setMode('hvnc');
    setWebrtcState('connecting');
    // Fire-and-forget — agent auto-starts streaming on patch load
    cmd('hvnc_stream/start', { fps: 30, quality: 50 });
    _openStreamWS(`/hvnc/stream/view/${deviceId}`, hvncWsRef, 'hvnc', hvncImgRef, hvncStoppingRef);
  }, [deviceId, _openStreamWS]);  // eslint-disable-line

  const stopHVNC = useCallback(async () => {
    hvncStoppingRef.current = true;
    if (hvncWsRef.current) { hvncWsRef.current.close(); hvncWsRef.current = null; }
    await cmd('hvnc_stream/stop');
    setMode('idle'); setWebrtcState('');
    setHvncInitState('idle');
    toast('HVNC stopped');
  }, []);  // eslint-disable-line

  const initHVNCDesktop = useCallback(async () => {
    setHvncInitState('running');
    toast('Initialising hidden desktop…');
    const ok = await cmd('hvnc_init', {});
    if (ok) {
      setHvncInitState('done');
      toast.success('Hidden desktop ready — now click HVNC to stream');
    } else {
      setHvncInitState('error');
      toast.error('Desktop init failed — check agent logs');
    }
  }, []);  // eslint-disable-line

  // ── Quick RDP Access ─────────────────────────────────────────────────────────
  const createRdpAccess = async () => {
    setRdpAccessLoading(true);
    setRdpAccessCreds(null);
    try {
      const res = await fetch(API + '/cmd/' + deviceId + '/rdp/create_access', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await res.json();
      if (res.ok && data.username) {
        setRdpAccessCreds({
          username: data.username, password: data.password,
          public_ip: data.public_ip, pending: data.pending,
          tunnel_port: data.tunnel_port,
          tunnel_status: data.tunnel_port ? 'starting' : 'none',
        });
        if (data.pending) {
          toast('Credentials generated. Agent offline — RDP will be configured when it reconnects.', { icon: '⏳', duration: 6000 });
        } else {
          toast.success('RDP access created — tunnel starting...');
        }
      } else {
        toast.error(data.detail || 'Failed to create RDP access');
      }
    } catch (e) {
      toast.error(e.message || 'Device offline');
    }
    setRdpAccessLoading(false);
  };
  const removeRdpAccess = async () => {
    await cmd('rdp/remove_access');
    setRdpAccessCreds(null);
    localStorage.removeItem(rdpStorageKey);
    toast.success('RDP access removed');
  };
  const copyRdpCreds = () => {
    if (!rdpAccessCreds) return;
    let text = `Username: ${rdpAccessCreds.username}\nPassword: ${rdpAccessCreds.password}`;
    if (rdpAccessCreds.local_ip) text += `\nLocal IP: ${rdpAccessCreds.local_ip}`;
    if (rdpAccessCreds.public_ip) text += `\nPublic IP: ${rdpAccessCreds.public_ip}`;
    navigator.clipboard.writeText(text).then(() => toast.success('Credentials copied'));
  };

  // ── RDP helpers ─────────────────────────────────────────────────────────────
  const _launchRDP = async (rdpMode) => {
    pendingRdpMode.current = rdpMode;
    setMode('rdp');
    setRdpPhaseBoth('tunnel');

    // If tunnel is already ready from Create RDP Access, connect directly
    if (rdpAccessCreds && rdpAccessCreds.tunnel_status === 'ready' && rdpAccessCreds.tunnel_port) {
      toast.success('Tunnel already ready — connecting...');
      connectGuacamole(rdpAccessCreds.tunnel_port, false);
      return;
    }

    // Skip rdp/prepare if Create RDP Access already configured it
    if (!rdpAccessCreds) {
      toast('Preparing RDP...');
      const ok = await cmd('rdp/prepare');
      if (!ok) { setRdpPhaseBoth('error'); setMode('idle'); return; }
    }
    toast('Establishing tunnel...');
    const rdpVpsPort = 13000 + Math.floor(Math.random() * 1000);
    const ok2 = await cmd('tunnel/start', { rdp_port: 3389, vps_port: rdpVpsPort });
    if (!ok2) {
      toast.error('Tunnel failed — agent may be offline. Use an external RDP client with the credentials above.');
      setRdpPhaseBoth('error'); setMode('idle');
    }
  };

  const startRDPSession = () => {
    // If Create RDP Access was used, auto-fill those credentials
    if (!credsStored && rdpAccessCreds) {
      rdpCredsRef.current = { username: rdpAccessCreds.username, password: rdpAccessCreds.password };
      setCredsStored(true);
      setCredUsername(rdpAccessCreds.username);
      setCredPassword(rdpAccessCreds.password);
    }
    if (credsStored || rdpAccessCreds) { _launchRDP('session'); return; }
    pendingRdpMode.current = 'session';
    setShowCreds(true);
  };

  // Screen Share uses VNC — zero credentials, agent-run server
  const startScreenShare = async () => {
    pendingRdpMode.current = 'share';
    setMode('rdp');
    setRdpPhaseBoth('tunnel');
    toast('Starting Screen Share...');
    const ok = await cmd('vnc/start');
    if (!ok) { setRdpPhaseBoth('error'); setMode('idle'); return; }
    const ok2 = await cmd('vnc_tunnel/start', { vnc_port: 5901 });
    if (!ok2) { setRdpPhaseBoth('error'); setMode('idle'); }
    // connectVNC() called by vnc_tunnel_ready live event
  };

  const _submitCreds = () => {
    rdpCredsRef.current = { username: credUsername, password: credPassword };
    setCredsStored(true);
    setShowCreds(false);
    _launchRDP(pendingRdpMode.current);
  };

  const stopRDP = () => {
    if (rdpClientRef.current) {
      try { rdpClientRef.current.disconnect(); } catch (_) {}
      rdpClientRef.current = null;
    }
    if (rdpKeyRef.current) {
      try { rdpKeyRef.current.onkeydown = null; rdpKeyRef.current.onkeyup = null; } catch (_) {}
      rdpKeyRef.current = null;
    }
    if (pendingRdpMode.current === 'share') {
      cmd('vnc/stop'); cmd('vnc_tunnel/stop');
    } else {
      cmd('tunnel/stop');
    }
    setRdpPhaseBoth('idle');
    setMode('idle');
    toast('Session ended');
  };

  // ── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      fetch(API + '/cmd/' + deviceId + '/stream/stop',      { method: 'POST', headers: authHeaders() }).catch(() => {});
      fetch(API + '/cmd/' + deviceId + '/webrtc/stop',      { method: 'POST', headers: authHeaders() }).catch(() => {});
      fetch(API + '/cmd/' + deviceId + '/tunnel/stop',      { method: 'POST', headers: authHeaders() }).catch(() => {});
      fetch(API + '/cmd/' + deviceId + '/vnc/stop',         { method: 'POST', headers: authHeaders() }).catch(() => {});
      fetch(API + '/cmd/' + deviceId + '/vnc_tunnel/stop',  { method: 'POST', headers: authHeaders() }).catch(() => {});
      fetch(API + '/cmd/' + deviceId + '/vnc_stream/stop',  { method: 'POST', headers: authHeaders() }).catch(() => {});
      fetch(API + '/cmd/' + deviceId + '/hvnc_stream/stop', { method: 'POST', headers: authHeaders() }).catch(() => {});
      if (vncWsRef.current)    { try { vncWsRef.current.close(); } catch (_) {} vncWsRef.current = null; }
      if (hvncWsRef.current)   { try { hvncWsRef.current.close(); } catch (_) {} hvncWsRef.current = null; }
      if (vncImgRef.current?._blobUrl)  URL.revokeObjectURL(vncImgRef.current._blobUrl);
      if (hvncImgRef.current?._blobUrl) URL.revokeObjectURL(hvncImgRef.current._blobUrl);
      if (dcRef.current)       { try { dcRef.current.close(); } catch (_) {} dcRef.current = null; }
      if (pcRef.current)       { pcRef.current.close(); pcRef.current = null; }
      if (rdpClientRef.current){ try { rdpClientRef.current.disconnect(); } catch (_) {} rdpClientRef.current = null; }
      if (rdpKeyRef.current)   { rdpKeyRef.current.onkeydown = null; rdpKeyRef.current.onkeyup = null; }
    };
  }, [deviceId]);

  // ── Remote key helpers (for WebRTC mode) ───────────────────────────────────
  const hotkey = async (combo) => { await cmd('remote/keyboard', { action: 'hotkey', key: combo }); toast.success('Sent: ' + combo); };
  const toggleBlank = async () => { await cmd('screen/blank', { enable: !blanked }); setBlanked(b => !b); toast.success(blanked ? 'Screen restored' : 'Screen blanked'); };

  // ── Colours ─────────────────────────────────────────────────────────────────
  const streamColor = (mode === 'webrtc' || mode === 'stream' || mode === 'vnc_stream' || mode === 'hvnc')
    ? (webrtcState === 'connected' ? 'var(--green)' : '#eab308')
    : mode === 'rdp' ? (rdpPhase === 'connected' ? '#0ea5e9' : '#eab308')
    : 'var(--border)';

  const rdpLabel = pendingRdpMode.current === 'share' ? '📺 Screen Share' : '🖥 RDP Session';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Popup title bar (only shown when opened in a separate window) ── */}
      {isPopup && (
        <div style={{ padding: '6px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexShrink: 0 }}>
          <span style={{ fontWeight: 600, color: 'var(--accent)' }}>💻 {deviceName}</span>
          <span style={{ color: 'var(--muted)' }}>›</span>
          <span style={{ color: 'var(--text)' }}>Remote Control</span>
          <button onClick={() => window.close()} style={{ ...btn('var(--red)'), marginLeft: 'auto', padding: '4px 10px' }}>✕ Close</button>
        </div>
      )}

      {/* ── Credentials Modal ── */}
      {showCreds && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 10, padding: 28, width: 320 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
              {pendingRdpMode.current === 'share' ? '📺 Screen Share' : '🖥 RDP Session'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
              Enter Windows credentials — saved for this session.
            </div>
            <label style={{ fontSize: 12, color: 'var(--muted)' }}>Username</label>
            <input autoFocus value={credUsername} onChange={e => setCredUsername(e.target.value)}
              placeholder="e.g. Administrator"
              style={{ ...inputStyle, marginBottom: 12, marginTop: 4, display: 'block' }} />
            <label style={{ fontSize: 12, color: 'var(--muted)' }}>Password</label>
            <input type="password" value={credPassword} onChange={e => setCredPassword(e.target.value)}
              placeholder="Windows password"
              onKeyDown={e => e.key === 'Enter' && _submitCreds()}
              style={{ ...inputStyle, marginBottom: 18, marginTop: 4, display: 'block' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreds(false)} style={btn()}>Cancel</button>
              <button onClick={_submitCreds} style={btn('#0ea5e9')}>Connect</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Top toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontWeight: 700, marginRight: 4 }}>Remote Control</h2>

        {mode !== 'rdp' ? (
          <>
            {mode !== 'vnc_stream'
              ? <button onClick={startVNCStream} style={btn('#10b981')}>📺 VNC Stream</button>
              : <button onClick={stopVNCStream}  style={btn('var(--red)')}>⏹ Stop VNC</button>}
            {mode !== 'hvnc' ? (<>
              <button
                onClick={initHVNCDesktop}
                disabled={hvncInitState === 'running'}
                title="Create hidden desktop + start shell first, then click HVNC"
                style={btn(hvncInitState === 'done' ? '#059669' : hvncInitState === 'error' ? '#dc2626' : '#4b5563')}>
                {hvncInitState === 'running' ? '⏳ Init…' : hvncInitState === 'done' ? '✅ Desktop Ready' : '🖥 Init Desktop'}
              </button>
              <button onClick={startHVNC} style={btn('#7c3aed')}>👻 HVNC</button>
            </>) : (
              <button onClick={stopHVNC} style={btn('var(--red)')}>⏹ Stop HVNC</button>
            )}
            <button onClick={startRDPSession}  style={btn('#0ea5e9')}>🖥 RDP Session</button>
            <button onClick={createRdpAccess} disabled={rdpAccessLoading} style={btn('#6366f1')}>
              {rdpAccessLoading ? '⏳ Creating...' : rdpAccessCreds ? '🔑 Reset RDP Access' : '🔑 Create RDP Access'}
            </button>
            {rdpAccessCreds && (
              <button onClick={removeRdpAccess} style={btn('#ef4444')}>🗑 Remove RDP Access</button>
            )}
          </>
        ) : (
          <button onClick={stopRDP} style={btn('var(--red)')}>⏹ End {rdpLabel}</button>
        )}

        {mode !== 'rdp' && (
          mode === 'stream'
            ? <button onClick={stopLiveStream} style={btn('var(--red)')}>⏹ Stop Stream</button>
            : <button onClick={startLiveStream} style={btn('#f59e0b')}>📡 Live Stream</button>
        )}

        {mode !== 'rdp' && mode !== 'stream' && (
          mode !== 'webrtc'
            ? <button type="button" onClick={startWebRTC} style={btn('#8b5cf6')}>⚡ WebRTC</button>
            : <button type="button" onClick={stopWebRTC}  style={btn('var(--red)')}>⏹ Stop WebRTC</button>
        )}

        {mode !== 'rdp' && mode !== 'webrtc' && (
          <select value={streamPreset} onChange={e => setStreamPreset(e.target.value)}
                  style={{ ...inputStyle, width: 120, padding: '4px 6px', fontSize: 11 }}>
            <option value="normal">Stream: 5fps</option>
            <option value="better">Stream: 8fps</option>
            <option value="best">Stream: 12fps</option>
            <option value="super">Stream: 20fps</option>
            <option value="auto">Stream: Auto</option>
          </select>
        )}

        {mode !== 'rdp' && mode !== 'stream' && mode !== 'webrtc' && (
          <select value={webrtcMode} onChange={e => setWebrtcMode(e.target.value)}
                  style={{ ...inputStyle, width: 120, padding: '4px 6px', fontSize: 11 }}>
            <option value="auto">RTC: Auto</option>
            <option value="direct">RTC: Direct</option>
            <option value="relay">RTC: Relay</option>
            <option value="jpeg">RTC: JPEG</option>
          </select>
        )}

        {mode !== 'rdp' && (
          <>
            <button onClick={() => cmd('screenshot')} style={btn()}>📸 Snapshot</button>
            <button onClick={toggleBlank} style={btn(blanked ? '#eab308' : undefined)}>
              {blanked ? '🖥 Restore Screen' : '⬛ Blank Screen'}
            </button>
            <button onClick={async () => {
              const text = await navigator.clipboard.readText().catch(() => '');
              if (text) { await cmd('clipboard/set', { text }); toast.success('Pasted to remote clipboard'); }
              else toast.error('Local clipboard is empty');
            }} style={{ ...btn('#0ea5e9'), fontSize: 11 }}>📋 Paste to PC</button>
            <button onClick={async () => {
              await cmd('clipboard/get');
              toast('Fetching remote clipboard...');
            }} style={{ ...btn('#0ea5e9'), fontSize: 11 }}>📋 Copy from PC</button>
            {['ctrl+c','ctrl+v','ctrl+z','alt+f4','ctrl+alt+del','win'].map(k => (
              <button key={k} onClick={() => hotkey(k)} style={{ ...btn(), fontSize: 11 }}>{k}</button>
            ))}
          </>
        )}

        {credsStored && mode !== 'rdp' && (
          <button onClick={() => { setCredsStored(false); setCredUsername(''); setCredPassword(''); }} style={{ ...btn(), fontSize: 11, marginLeft: 'auto' }}>
            🔑 Change Credentials
          </button>
        )}

        <button onClick={toggleFullscreen} style={btn()}>
          {fullscreen ? '✕ Exit Fullscreen' : '⛶ Fullscreen'}
        </button>

        {!isPopup && (
          <button onClick={popOut} style={btn()} title="Open in separate window">⤢ Pop out</button>
        )}

        {mode !== 'idle' && (
          <span style={{ marginLeft: 'auto', fontSize: 12, fontFamily: 'monospace', color: streamColor }}>
            {mode === 'rdp'
              ? (rdpLabel + ' ' + (rdpPhase === 'connected' ? '● LIVE' : rdpPhase))
              : mode === 'webrtc'
                ? ('⚡ ' + (webrtcState === 'connected' ? '● LIVE' : webrtcState))
                : mode === 'stream'
                  ? ('📡 ' + (webrtcState === 'connected' ? '● LIVE' : webrtcState))
                  : mode === 'vnc_stream'
                    ? ('📺 VNC ' + (webrtcState === 'connected' ? '● LIVE' : webrtcState))
                    : mode === 'hvnc'
                      ? ('👻 HVNC ' + (webrtcState === 'connected' ? '● LIVE' : webrtcState))
                      : '● LIVE'}
            {(mode === 'webrtc' || mode === 'stream' || mode === 'vnc_stream' || mode === 'hvnc') && <>&nbsp; {fps} fps &nbsp; {frameCount} frames</>}
          </span>
        )}
      </div>

      {/* ── Main area ── */}
      <div style={{ display: 'grid', gridTemplateColumns: mode === 'rdp' ? '1fr' : '1fr 260px', gap: 12, flex: 1, minHeight: 0 }}>

        <div ref={containerRef}
             style={{ background: '#0a1628', borderRadius: 8, overflow: 'hidden',
                      border: '2px solid ' + streamColor,
                      position: 'relative', display: 'flex',
                      alignItems: 'center', justifyContent: 'center' }}>

          {/* Guacamole RDP/Screen Share canvas */}
          {mode === 'rdp' && (
            <div ref={rdpContRef}
                 style={{ width: '100%', height: '100%', overflow: 'hidden',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: '#000', position: 'relative', maxHeight: '100%' }}>
              {/* Guacamole canvas — NOT managed by React, safe to innerHTML */}
              <div ref={guacCanvasRef}
                   style={{ width: '100%', height: '100%', overflow: 'hidden',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
              {/* Overlay — managed by React, floats on top */}
              {(rdpPhase !== 'connected' || !rdpLoaded) && (
                <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 40,
                              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center', zIndex: 5, background: '#000' }}>
                  {rdpPhase === 'tunnel'     && <><div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div><div>Establishing tunnel...</div></>}
                  {rdpPhase === 'connecting' && <><div style={{ fontSize: 24, marginBottom: 8 }}>🔗</div><div>Connecting to desktop...</div></>}
                  {rdpPhase === 'connected' && !rdpLoaded && <><div style={{ fontSize: 24, marginBottom: 8 }}>🖥</div><div>Loading desktop... please wait</div></>}
                  {rdpPhase === 'error'      && (
                    <><div style={{ fontSize: 24, marginBottom: 8 }}>❌</div>
                      <div style={{ color: 'var(--red)' }}>Connection failed</div>
                      <div style={{ fontSize: 11, marginTop: 4, color: 'var(--muted)' }}>Check credentials, RDP enabled, and guacd running</div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                        <button onClick={() => { setCredsStored(false); setMode('idle'); setRdpPhaseBoth('idle'); }} style={btn()}>🔑 Re-enter Credentials</button>
                        <button onClick={() => pendingRdpMode.current === 'share' ? startScreenShare() : startRDPSession()} style={btn('#eab308')}>↺ Retry</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* VNC stream frame display — dedicated, no HVNC mixing */}
          <img ref={vncImgRef} alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain',
                     display: mode === 'vnc_stream' ? 'block' : 'none',
                     cursor: 'crosshair', userSelect: 'none' }}
            onMouseMove={e => {
              const now = Date.now();
              if (now - lastMoveRef.current < 33) return;
              lastMoveRef.current = now;
              const { nx, ny } = _getContainCoords(e, vncImgRef.current);
              sendInput({ event: 'mousemove', nx, ny });
            }}
            onMouseDown={e => {
              const { nx, ny } = _getContainCoords(e, vncImgRef.current);
              sendInput({ event: 'mousedown', button: e.button, nx, ny });
            }}
            onMouseUp={e => {
              const { nx, ny } = _getContainCoords(e, vncImgRef.current);
              sendInput({ event: 'mouseup', button: e.button, nx, ny });
            }}
            onWheel={e => {
              const { nx, ny } = _getContainCoords(e, vncImgRef.current);
              sendInput({ event: 'wheel', dy: e.deltaY, nx, ny });
            }}
            onContextMenu={e => e.preventDefault()}
          />

          {/* HVNC stream frame display — fully separate from VNC, own coordinate system */}
          <img ref={hvncImgRef} alt=""
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain',
                     display: mode === 'hvnc' ? 'block' : 'none',
                     cursor: 'none', userSelect: 'none' }}
            onMouseMove={e => {
              const now = Date.now();
              if (now - lastMoveRef.current < 33) return;
              lastMoveRef.current = now;
              const { nx, ny } = _getContainCoords(e, hvncImgRef.current);
              sendInput({ event: 'mousemove', nx, ny });
            }}
            onMouseDown={e => {
              e.preventDefault(); // stop browser from starting an image drag
              const { nx, ny } = _getContainCoords(e, hvncImgRef.current);
              sendInput({ event: 'mousedown', button: e.button, nx, ny }); // must run before setPointerCapture which can throw on invalid pointerId
              try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
            }}
            onMouseUp={e => {
              const { nx, ny } = _getContainCoords(e, hvncImgRef.current);
              sendInput({ event: 'mouseup', button: e.button, nx, ny });
            }}
            onDoubleClick={e => {
              const { nx, ny } = _getContainCoords(e, hvncImgRef.current);
              sendInput({ event: 'dblclick', button: e.button, nx, ny });
            }}
            onWheel={e => {
              const { nx, ny } = _getContainCoords(e, hvncImgRef.current);
              sendInput({ event: 'wheel', dy: e.deltaY, nx, ny });
            }}
            onContextMenu={e => e.preventDefault()}
            onDragStart={e => e.preventDefault()}
          />

          {/* JPEG Live Stream */}
          <img ref={streamImgRef} alt=""
            style={{ width: '100%', display: mode === 'stream' ? 'block' : 'none',
                     cursor: 'crosshair', userSelect: 'none' }}
            onMouseMove={e => {
              if (mode !== 'stream') return;
              const now = Date.now();
              if (now - lastMoveRef.current < 33) return;
              lastMoveRef.current = now;
              const r = streamImgRef.current.getBoundingClientRect();
              sendInput({ event: 'mousemove', nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onMouseDown={e => {
              if (mode !== 'stream') return;
              const r = streamImgRef.current.getBoundingClientRect();
              sendInput({ event: 'mousedown', button: e.button, nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onMouseUp={e => {
              if (mode !== 'stream') return;
              const r = streamImgRef.current.getBoundingClientRect();
              sendInput({ event: 'mouseup', button: e.button, nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onWheel={e => {
              if (mode !== 'stream') return;
              const r = streamImgRef.current.getBoundingClientRect();
              sendInput({ event: 'wheel', dy: e.deltaY, nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onContextMenu={e => e.preventDefault()}
          />

          {/* WebRTC video */}
          <video ref={videoRef} autoPlay playsInline muted
            style={{ width: '100%', display: mode === 'webrtc' ? 'block' : 'none',
                     cursor: 'crosshair', userSelect: 'none' }}
            onMouseMove={e => {
              if (mode !== 'webrtc') return;
              const now = Date.now();
              if (now - lastMoveRef.current < 33) return; // max 30/sec
              lastMoveRef.current = now;
              const v = videoRef.current; if (!v) return;
              const r = v.getBoundingClientRect();
              sendInput({ event: 'mousemove', nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onMouseDown={e => {
              if (mode !== 'webrtc') return;
              const v = videoRef.current; if (!v) return;
              const r = v.getBoundingClientRect();
              sendInput({ event: 'mousedown', button: e.button, nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onMouseUp={e => {
              if (mode !== 'webrtc') return;
              const v = videoRef.current; if (!v) return;
              const r = v.getBoundingClientRect();
              sendInput({ event: 'mouseup', button: e.button, nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onWheel={e => {
              if (mode !== 'webrtc') return;
              const v = videoRef.current; if (!v) return;
              const r = v.getBoundingClientRect();
              sendInput({ event: 'wheel', dy: e.deltaY, nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height });
            }}
            onContextMenu={e => e.preventDefault()}
          />

          {/* Idle placeholder */}
          {mode === 'idle' && (
            <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: 40, lineHeight: 2 }}>
              <strong style={{ color: '#10b981' }}>📺 VNC Stream</strong> — direct JPEG canvas, smooth &amp; low-latency<br/>
              <strong style={{ color: '#7c3aed' }}>👻 HVNC</strong> — hidden isolated Windows desktop, invisible to user<br/>
              <strong style={{ color: '#0ea5e9' }}>🖥 RDP Session</strong> — Windows RDP via guacd tunnel<br/>
              <strong style={{ color: '#f59e0b' }}>📡 Live Stream</strong> — 5fps JPEG push, works on any network<br/>
              <strong style={{ color: '#8b5cf6' }}>⚡ WebRTC</strong> — P2P H.264, requires aiortc on agent<br/>
              <strong>📸 Snapshot</strong> — single screenshot
            </div>
          )}

          {/* Live badge */}
          {mode !== 'idle' && (
            <div style={{ position: 'absolute', top: 8, left: 8,
                          background: mode === 'rdp'
                            ? 'rgba(14,165,233,.9)'
                            : mode === 'stream' ? 'rgba(245,158,11,.9)'
                            : mode === 'vnc_stream' ? 'rgba(16,185,129,.9)'
                            : mode === 'hvnc' ? 'rgba(124,58,237,.9)'
                            : 'rgba(139,92,246,.9)',
                          color: '#fff', fontSize: 10, fontWeight: 700,
                          padding: '2px 7px', borderRadius: 3, letterSpacing: 1.5 }}>
              {mode === 'rdp' ? 'RDP' : mode === 'stream' ? 'LIVE' : mode === 'vnc_stream' ? 'VNC' : mode === 'hvnc' ? 'HVNC' : 'WEBRTC'}
            </div>
          )}

          {/* Floating End button removed — using fixed-position one outside container */}

          <button onClick={toggleFullscreen}
            style={{ position: 'absolute', top: 8, right: 8,
                     background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.3)',
                     color: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 16, padding: '2px 8px' }}>
            {fullscreen ? '✕' : '⛶'}
          </button>
        </div>

        {/* ── Right sidebar (hidden in RDP mode) ── */}
        {mode !== 'rdp' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>

            {/* RDP Access Credentials */}
            {rdpAccessCreds && (
              <Panel title={
                rdpAccessCreds.tunnel_status === 'ready' ? '✅ RDP Ready — Connect Now'
                : rdpAccessCreds.verified ? '✅ RDP Verified'
                : rdpAccessCreds.pending ? '⏳ RDP Pending'
                : rdpAccessCreds.tunnel_status === 'starting' ? '🔄 RDP — Tunnel Starting...'
                : '🔑 RDP Access'
              }>
                <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                  <div><span style={{ color: 'var(--muted)' }}>User:</span> <strong>{rdpAccessCreds.username}</strong></div>
                  <div><span style={{ color: 'var(--muted)' }}>Pass:</span> <strong>{rdpAccessCreds.password}</strong></div>
                  {rdpAccessCreds.local_ip && (
                    <div><span style={{ color: 'var(--muted)' }}>Local:</span> <strong style={{ color: '#38bdf8' }}>{rdpAccessCreds.local_ip}</strong></div>
                  )}
                  {rdpAccessCreds.public_ip && (
                    <div><span style={{ color: 'var(--muted)' }}>Public:</span> <strong style={{ color: '#38bdf8' }}>{rdpAccessCreds.public_ip}</strong></div>
                  )}
                </div>
                {/* Tunnel status indicator */}
                <div style={{ fontSize: 11, marginTop: 6, padding: '4px 8px', borderRadius: 4,
                  background: rdpAccessCreds.tunnel_status === 'ready' ? '#052e1680'
                    : rdpAccessCreds.tunnel_status === 'starting' ? '#422006'
                    : '#1e1b4b80',
                  color: rdpAccessCreds.tunnel_status === 'ready' ? '#4ade80'
                    : rdpAccessCreds.tunnel_status === 'starting' ? '#fbbf24'
                    : '#94a3b8',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%',
                    background: rdpAccessCreds.tunnel_status === 'ready' ? '#22c55e'
                      : rdpAccessCreds.tunnel_status === 'starting' ? '#f59e0b'
                      : '#64748b',
                  }} />
                  {rdpAccessCreds.tunnel_status === 'ready' ? 'Tunnel Ready — Click RDP Session to connect'
                    : rdpAccessCreds.tunnel_status === 'starting' ? 'Tunnel starting...'
                    : 'Tunnel not started'}
                  {rdpAccessCreds.tunnel_port && (
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#64748b' }}>:{rdpAccessCreds.tunnel_port}</span>
                  )}
                </div>
                {rdpAccessCreds.steps && (
                  <div style={{ fontSize: 10, marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {rdpAccessCreds.steps.map((s, i) => (
                      <span key={i} style={{ padding: '2px 6px', borderRadius: 4,
                        background: s.includes('OK') ? '#052e1680' : '#450a0a80',
                        color: s.includes('OK') ? '#4ade80' : '#f87171', fontSize: 10 }}>
                        {i === 0 ? 'RDP' : i === 1 ? 'User' : 'Verify'} {s.includes('OK') ? '✓' : '✗'}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={copyRdpCreds} style={{ ...btn('#6366f1'), flex: 1, fontSize: 11, padding: '4px 8px' }}>📋 Copy</button>
                  <button onClick={removeRdpAccess} style={{ ...btn('#dc2626'), flex: 1, fontSize: 11, padding: '4px 8px' }}>🗑 Remove</button>
                </div>
              </Panel>
            )}

            <Panel title="WebRTC Diagnostics">
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.7 }}>
                <div>Offer: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.offerReceived ? 'received' : 'pending'}</strong></div>
                <div>Offer time: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.offerAt ? new Date(webrtcDiag.offerAt).toLocaleTimeString() : '-'}</strong></div>
                <div>Answer time: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.answerAt ? new Date(webrtcDiag.answerAt).toLocaleTimeString() : '-'}</strong></div>
                <div>Signaling: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.signalingState}</strong></div>
                <div>ICE gather: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.iceGatheringState}</strong></div>
                <div>ICE conn: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.iceConnectionState}</strong></div>
                <div>Peer conn: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.connectionState}</strong></div>
                <div>Path: <strong style={{ color: webrtcDiag.selectedPath === 'relay' ? '#f59e0b' : '#22c55e' }}>{webrtcDiag.selectedPath}</strong></div>
                <div>Candidate types: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.selectedLocalType || '-'} → {webrtcDiag.selectedRemoteType || '-'}</strong></div>
                <div>Stats: <strong style={{ color: 'var(--text)' }}>{webrtcDiag.statsAt ? new Date(webrtcDiag.statsAt).toLocaleTimeString() : '-'}</strong></div>
                {webrtcDiag.error && (
                  <div style={{ marginTop: 6, color: '#f87171' }}>Error: {webrtcDiag.error}</div>
                )}
              </div>
            </Panel>

            <Panel title="Power">
              {[['Lock','lock',false],['Sleep','sleep',false],['Restart','restart',true],['Shutdown','shutdown',true]].map(([label, action, danger]) => (
                <button key={action}
                  onClick={async () => { if (danger && !window.confirm(label + ' this device?')) return; await cmd('power', { action }); toast.success(label + ' sent'); }}
                  style={{ ...btn(danger ? 'var(--red)' : undefined), display: 'block', width: '100%', marginBottom: 6, textAlign: 'left' }}>
                  {label}
                </button>
              ))}
            </Panel>

            {!!deviceId && (
              <Panel title="HVNC — Launch Process">
                {hvncInitState !== 'done' && (
                  <div style={{ fontSize: 11, background: 'rgba(245,158,11,.12)',
                                border: '1px solid rgba(245,158,11,.3)', borderRadius: 6,
                                padding: '6px 8px', marginBottom: 8, color: '#f59e0b' }}>
                    ⚠ Desktop not initialised — apps may not render.{' '}
                    <span onClick={initHVNCDesktop}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                      Init now
                    </span>
                  </div>
                )}
                {/* Quick-launch grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>
                  {[
                    { label: '📝 Notepad',     cmd_: 'notepad.exe' },
                    { label: '🖩 Calculator',  cmd_: 'calc.exe' },
                    { label: '⬛ CMD',          cmd_: 'cmd.exe' },
                    { label: '🔵 PowerShell',  cmd_: 'powershell.exe' },
                    { label: '📁 Explorer',    cmd_: 'explorer.exe' },
                    { label: '🎨 Paint',       cmd_: 'mspaint.exe' },
                    { label: '🌐 Edge',        cmd_: 'msedge.exe' },
                    { label: '🌐 Chrome',      cmd_: 'chrome.exe' },
                    { label: '🔧 Task Mgr',    cmd_: 'taskmgr.exe' },
                    { label: '🔑 Regedit',     cmd_: 'regedit.exe' },
                    { label: '⚙ Control Panel',cmd_: 'control.exe' },
                    { label: '📡 MSConfig',    cmd_: 'msconfig.exe' },
                  ].map(({ label, cmd_ }) => (
                    <button key={cmd_}
                      onClick={async () => {
                        setHvncCmd(cmd_);
                        const ok = await cmd('hvnc_run', { cmd: cmd_ });
                        if (ok) toast.success(`Launched: ${cmd_}`);
                      }}
                      style={{ ...btn('#4b5563'), fontSize: 11, padding: '5px 6px',
                               textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden',
                               textOverflow: 'ellipsis' }}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Custom command */}
                <input
                  value={hvncCmd}
                  onChange={e => setHvncCmd(e.target.value)}
                  onKeyDown={async e => {
                    if (e.key !== 'Enter') return;
                    const ok = await cmd('hvnc_run', { cmd: hvncCmd });
                    if (ok) toast.success(`Launched: ${hvncCmd}`);
                  }}
                  placeholder="Custom command…"
                  style={{ ...inputStyle, fontSize: 12, marginBottom: 6 }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={async () => {
                      const ok = await cmd('hvnc_run', { cmd: hvncCmd });
                      if (ok) toast.success(`Launched: ${hvncCmd}`);
                    }}
                    style={{ ...btn('#7c3aed'), flex: 1, fontSize: 12 }}>
                    ▶ Launch
                  </button>
                  <button onClick={initHVNCDesktop}
                    disabled={hvncInitState === 'running'}
                    title="Re-init desktop"
                    style={{ ...btn('#4b5563'), fontSize: 12, padding: '6px 10px' }}>
                    🖥
                  </button>
                </div>
              </Panel>
            )}

            <Panel title="Stream Mode">
              <div style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.8 }}>
                <div><strong style={{ color: '#10b981' }}>📺 VNC</strong> — direct JPEG canvas</div>
                <div><strong style={{ color: '#7c3aed' }}>👻 HVNC</strong> — hidden desktop</div>
                <div><strong style={{ color: '#0ea5e9' }}>🖥 RDP</strong> — new desktop via guacd</div>
                <div><strong style={{ color: '#f59e0b' }}>📡 Stream</strong> — JPEG ({streamPreset})</div>
                <div><strong style={{ color: '#8b5cf6' }}>⚡ WebRTC</strong> — {webrtcMode}</div>
              </div>
            </Panel>

          </div>
        )}
      </div>
    </div>
  );
}

function _getContainCoords(e, imgEl) {
  const r = imgEl.getBoundingClientRect();
  const nw = imgEl.naturalWidth  || r.width;
  const nh = imgEl.naturalHeight || r.height;
  const scale = Math.min(r.width / nw, r.height / nh);
  const rw = nw * scale;
  const rh = nh * scale;
  const ox = (r.width  - rw) / 2;
  const oy = (r.height - rh) / 2;
  return {
    nx: Math.max(0, Math.min(1, (e.clientX - r.left - ox) / rw)),
    ny: Math.max(0, Math.min(1, (e.clientY - r.top  - oy) / rh)),
  };
}

function Panel({ title, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: 12, flexShrink: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
                    letterSpacing: .5, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

const btn = (color) => ({
  padding: '6px 12px',
  background: color ? color + '22' : 'transparent',
  border: '1px solid ' + (color || 'var(--border)'),
  borderRadius: 6,
  color: color || 'var(--muted)',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: color ? 600 : 400,
});

const inputStyle = {
  width: '100%', padding: '7px 10px',
  background: 'var(--bg)', border: '1px solid var(--border)',
  borderRadius: 6, color: 'var(--text)', fontSize: 13,
};
